import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../services/reducers/homeSlice';
import { Sidebar } from '../Layout/Sidebar';
import restApi from '../../services/restApi';

// Types for TypeScript
interface Shelf {
  id: number;
  title: string;
  slug: string;
  productCount?: number;
  totalCount?: number;
  shelfProducts?: ShelfProduct[];
}

interface ShelfProduct {
  id: number;
  productId: number;
  shelfId: number;
  count: number;
  unitType: 'box' | 'roll';
  product?: Product;
  shelf?: Shelf;
}

interface Product {
  id: number;
  title: string;
  titleEng?: string;
  sku: string;
  model?: string;
  status: number;
  price: number;
  boxCount: number;
  rollCount: number;
  shortText?: string;
  longText?: string;
  accountingCode?: string;
  unitType: string;
  itemsPerBox?: number;
  itemsPerRoll?: number;
  shelfProducts?: ShelfProduct[];
}

interface ProductLocation {
  product: Product;
  shelves: Array<{
    shelf: Shelf;
    count: number;
    unitType: string;
  }>;
}

const Shelves = () => {
  const dispatch = useDispatch();
  const [viewMode, setViewMode] = useState('shelves'); // 'shelves' or 'products'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedShelf, setSelectedShelf] = useState<Shelf | null>(null);
  const [selectedLetter, setSelectedLetter] = useState<string>(''); // New state for selected letter
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [shelfProducts, setShelfProducts] = useState<ShelfProduct[]>([]);
  const [productLocations, setProductLocations] = useState<ProductLocation[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [displayMode, setDisplayMode] = useState('grid'); // 'grid' or 'list'

  // Available letters based on your Excel file
  const availableLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'K', 'L'];

  useEffect(() => {
    loadShelves();
  }, []);

  const loadShelves = async () => {
    dispatch(setLoading(true));
    try {
      const response = await restApi(process.env.REACT_APP_BASE_URL + '/admin/shelf/summary', true).get();
      if (response && response.data) {
        setShelves(response.data);
      }
    } catch (error) {
      console.error('Error loading shelves:', error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleShelfSelect = async (shelf: Shelf) => {
    setSelectedShelf(shelf);
    dispatch(setLoading(true));
    try {
      const response = await restApi(process.env.REACT_APP_BASE_URL + `/admin/shelf/${shelf.id}/products`, true).get();
      if (response && response.data) {
        setShelfProducts(response.data);
      }
    } catch (error) {
      console.error('Error loading shelf products:', error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleProductSearch = async () => {
    if (!searchQuery.trim()) return;

    dispatch(setLoading(true));
    try {
      const response = await restApi(process.env.REACT_APP_BASE_URL + '/admin/product', true).get({
        search: searchQuery
      });

      if (response && response.data) {
        // Filter to only include products that have shelf assignments
        const productsWithShelves = response.data.filter(
          (product: any) => product.shelfProducts && product.shelfProducts.length > 0
        );

        const productLocations: ProductLocation[] = productsWithShelves.map((product: any) => ({
          product,
          shelves: product.shelfProducts.map((sp: ShelfProduct) => ({
            shelf: sp.shelf || { id: sp.shelfId, title: `قفسه ${sp.shelfId}`, slug: `shelf-${sp.shelfId}` },
            count: sp.count,
            unitType: sp.unitType
          }))
        }));

        setProductLocations(productLocations);
      }
    } catch (error) {
      console.error('Error searching product locations:', error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  // Get shelves grouped by letter
  const shelvesGroupedByLetter = useMemo(() => {
    const grouped: { [key: string]: Shelf[] } = {};
    shelves.forEach(shelf => {
      const letter = shelf.title.charAt(0);
      if (!grouped[letter]) {
        grouped[letter] = [];
      }
      grouped[letter].push(shelf);
    });
    return grouped;
  }, [shelves]);

  // Get letter statistics
  const getLetterStats = (letter: string) => {
    const letterShelves = shelvesGroupedByLetter[letter] || [];
    const totalShelves = letterShelves.length;
    const totalProducts = letterShelves.reduce((sum, shelf) => sum + (shelf.productCount || 0), 0);
    const totalCount = letterShelves.reduce((sum, shelf) => sum + (shelf.totalCount || 0), 0);
    return { totalShelves, totalProducts, totalCount };
  };

  const filteredShelves = useMemo(() => {
    let filtered = shelves;

    // Filter by selected letter
    if (selectedLetter) {
      filtered = filtered.filter(shelf => shelf.title.charAt(0) === selectedLetter);
    }

    return filtered;
  }, [shelves, selectedLetter]);

  // Clear search results when switching modes or clearing search

  const getShelfColorBySection = (title: string) => {
    const letter = title.charAt(0);
    // Brand palette only — blue / sky blue / gray / near-black (no rainbow).
    const colorMap: { [key: string]: string } = {
      'A': '#1A6BFF',
      'B': '#4D8FFF',
      'C': '#7A8CA0',
      'D': '#3D4F6B',
      'E': '#0F1923',
      'F': '#1254D6',
      'G': '#93C5FD',
      'H': '#64748B',
      'K': '#334155',
      'L': '#B6C2D9',
    };
    return colorMap[letter] || '#7A8CA0';
  };

  return (
    <div className="dashboardBody">
      <Sidebar />
      <main className="dashBoardMain main">
        <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '1.5rem' }}>
          <div style={{ maxWidth: '80rem', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'white', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '0.25rem' }}>
                  <button
                    onClick={() => {
                      setViewMode('shelves');
                      setSelectedLetter('');
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: viewMode === 'shelves' ? '#1A6BFF' : 'transparent',
                      color: viewMode === 'shelves' ? 'white' : '#6b7280'
                    }}
                  >
                    مشاهده قفسه‌ها
                  </button>
                  <button
                    onClick={() => setViewMode('products')}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: viewMode === 'products' ? '#1A6BFF' : 'transparent',
                      color: viewMode === 'products' ? 'white' : '#6b7280'
                    }}
                  >
                    جستجوی محصول
                  </button>
                </div>
              </div>

              {/* Search Bar - Only show in products mode */}
              {viewMode === 'products' && (
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleProductSearch();
                      }
                    }}
                    placeholder="جستجوی محصول"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '0.75rem 2.5rem 0.75rem 1rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.75rem',
                      backgroundColor: 'white',
                      direction: 'rtl',
                      textAlign: 'right',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={handleProductSearch}
                    disabled={isSearching}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      padding: '0 1rem',
                      color: '#1A6BFF',
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {isSearching ? 'در حال جستجو...' : 'جستجو'}
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            {viewMode === 'shelves' ? (
              <div style={{ marginBottom: '1.5rem' }}>
                {/* Letter Selection or Shelf Display */}
                {!selectedLetter ? (
                  // Letter Grid Display
                  <div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1.5rem'
                    }}>
                      {availableLetters.map((letter) => {
                        const stats = getLetterStats(letter);
                        return (
                          <div
                            key={letter}
                            onClick={() => setSelectedLetter(letter)}
                            style={{
                              backgroundColor: 'white',
                              border: '2px solid #e5e7eb',
                              borderRadius: '1rem',
                              padding: '2rem',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              textAlign: 'center',
                              position: 'relative',
                              overflow: 'hidden'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = getShelfColorBySection(letter);
                              e.currentTarget.style.boxShadow = `0 8px 25px -8px ${getShelfColorBySection(letter)}40`;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = '#e5e7eb';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <div style={{
                              position: 'absolute',
                              inset: 0,
                              background: `linear-gradient(135deg, ${getShelfColorBySection(letter)}08, ${getShelfColorBySection(letter)}04)`,
                              opacity: 0.5
                            }} />

                            <div style={{ position: 'relative', zIndex: 1 }}>
                              <div style={{
                                width: '4rem',
                                height: '4rem',
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${getShelfColorBySection(letter)}, ${getShelfColorBySection(letter)}dd)`,
                                color: 'white',
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 1rem auto',
                                boxShadow: `0 4px 14px -6px ${getShelfColorBySection(letter)}60`
                              }}>
                                {letter}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', color: '#6b7280', fontSize: '0.875rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <span>{stats.totalShelves} قفسه</span>
                                  <span style={{ marginRight: '0.5rem' }}>📦</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <span>{stats.totalProducts} نوع محصول</span>
                                  <span style={{ marginRight: '0.5rem' }}>📋</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  // Shelves Display for Selected Letter
                  <div>
                    {/* Back Button and Section Header */}
                    <div style={{ marginBottom: '1.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <button
                            onClick={() => setSelectedLetter('')}
                            style={{
                              backgroundColor: '#f3f4f6',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.5rem',
                              padding: '0.5rem 1rem',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              marginLeft: '1rem',
                              display: 'flex',
                              alignItems: 'center',
                              color: '#6b7280'
                            }}
                          >
                            ← بازگشت
                          </button>
                        </div>

                        {/* Display Mode Toggle */}
                        <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'white', borderRadius: '0.5rem', border: '1px solid #e5e7eb', padding: '0.25rem' }}>
                          <button
                            onClick={() => setDisplayMode('grid')}
                            style={{
                              padding: '0.5rem',
                              borderRadius: '0.375rem',
                              border: 'none',
                              cursor: 'pointer',
                              backgroundColor: displayMode === 'grid' ? '#f3f4f6' : 'transparent',
                              color: displayMode === 'grid' ? '#111827' : '#6b7280'
                            }}
                          >
                            ⊞
                          </button>
                          <button
                            onClick={() => setDisplayMode('list')}
                            style={{
                              padding: '0.5rem',
                              borderRadius: '0.375rem',
                              border: 'none',
                              cursor: 'pointer',
                              backgroundColor: displayMode === 'list' ? '#f3f4f6' : 'transparent',
                              color: displayMode === 'list' ? '#111827' : '#6b7280'
                            }}
                          >
                            ☰
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Shelves Grid/List */}
                    <div style={displayMode === 'grid' ? {
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1rem'
                    } : {
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem'
                    }}>
                      {filteredShelves.map((shelf) => (
                        <div
                          key={shelf.id}
                          onClick={() => handleShelfSelect(shelf)}
                          style={{
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: '0.5rem',
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            padding: displayMode === 'grid' ? '1rem' : '0.75rem'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = getShelfColorBySection(shelf.title);
                            e.currentTarget.style.boxShadow = `0 4px 6px -1px ${getShelfColorBySection(shelf.title)}30`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div style={{
                            position: 'absolute',
                            inset: 0,
                            background: `linear-gradient(to bottom right, ${getShelfColorBySection(shelf.title)}15, ${getShelfColorBySection(shelf.title)}08)`,
                            opacity: 0.6
                          }} />

                          <div style={{ position: 'relative' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                              <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: displayMode === 'grid' ? '2rem' : '1.75rem',
                                height: displayMode === 'grid' ? '2rem' : '1.75rem',
                                borderRadius: '50%',
                                background: `linear-gradient(to bottom right, ${getShelfColorBySection(shelf.title)}, ${getShelfColorBySection(shelf.title)})`,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: displayMode === 'grid' ? '0.875rem' : '0.75rem'
                              }}>
                                {shelf.title.charAt(0)}
                              </div>
                              <span style={{ color: '#9ca3af', fontSize: '1rem' }}>←</span>
                            </div>

                            <h3 style={{
                              fontSize: displayMode === 'grid' ? '1rem' : '0.875rem',
                              fontWeight: 'bold',
                              color: '#111827',
                              marginBottom: '0.5rem'
                            }}>
                              {shelf.title}
                            </h3>

                            <div style={{ display: 'flex', flexDirection: displayMode === 'grid' ? 'column' : 'row', gap: displayMode === 'grid' ? '0.25rem' : '1rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: '#6b7280' }}>
                                <span style={{ marginRight: '0.25rem' }}> نوع محصول </span>
                                <span>{shelf.productCount || 0} </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginBottom: '1.5rem' }}>
                {/* Product Search Results */}
                {productLocations.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#111827' }}>نتایج جستجو</h2>
                    {productLocations.map((data, index) => (
                      <div key={index} style={{
                        backgroundColor: 'white',
                        borderRadius: '0.75rem',
                        border: '1px solid #e5e7eb',
                        padding: '1.5rem',
                        transition: 'box-shadow 0.3s ease'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                          <div>
                            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827', marginBottom: '0.25rem' }}>
                              {data.product.title || data.product.titleEng}
                            </h3>
                            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>SKU: {data.product.sku}</p>
                            {data.product.model && <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>مدل: {data.product.model}</p>}
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#dbeafe',
                            color: '#1d4ed8',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.875rem',
                            fontWeight: '500'
                          }}>
                            <span>{data.shelves.length} قفسه</span>
                            <span style={{ marginRight: '0.25rem' }}>📍</span>
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {data.shelves.map((location, locationIndex) => (
                            <div key={locationIndex} style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '0.75rem',
                              backgroundColor: '#f9fafb',
                              borderRadius: '0.5rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{
                                  width: '2rem',
                                  height: '2rem',
                                  borderRadius: '50%',
                                  background: `linear-gradient(to bottom right, ${getShelfColorBySection(location.shelf.title)}, ${getShelfColorBySection(location.shelf.title)})`,
                                  color: 'white',
                                  fontSize: '0.875rem',
                                  fontWeight: 'bold',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginLeft: '0.75rem'
                                }}>
                                  {location.shelf.title.charAt(0)}
                                </div>
                                <div>
                                  <p style={{ fontWeight: '500', color: '#111827' }}>{location.shelf.title}</p>
                                  <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>قفسه انبار</p>
                                </div>
                              </div>
                              <div style={{ textAlign: 'left' }}>
                                <p style={{ fontWeight: '600', color: '#111827' }}>{location.count}</p>
                                <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  {location.unitType === 'box' ? 'قوطی' : 'رولی'}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {searchQuery && productLocations.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📦</div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#111827', marginBottom: '0.5rem' }}>
                      محصولی یافت نشد
                    </h3>
                    <p style={{ color: '#6b7280' }}>محصولی با این مشخصات در قفسه‌ها یافت نشد</p>
                  </div>
                )}
              </div>
            )}

            {/* Shelf Detail Modal */}
            {selectedShelf && (
              <div style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 50,
                padding: '1rem'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '1rem',
                  maxWidth: '64rem',
                  width: '100%',
                  maxHeight: '90vh',
                  overflowY: 'auto'
                }}>
                  <div style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    padding: '1.5rem',
                    borderRadius: '1rem 1rem 0 0'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                          width: '3rem',
                          height: '3rem',
                          borderRadius: '50%',
                          background: `linear-gradient(to bottom right, ${getShelfColorBySection(selectedShelf.title)}, ${getShelfColorBySection(selectedShelf.title)})`,
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1.125rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginLeft: '1rem'
                        }}>
                          {selectedShelf.title.charAt(0)}
                        </div>
                        <div style={{marginLeft: '2rem'}}>
                          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827' }}>
                            قفسه {selectedShelf.title}
                          </h2>
                          <p style={{ color: '#6b7280' }}>{shelfProducts.length + " :"} تعداد محصولات قفسه</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedShelf(null)}
                        style={{
                          color: '#9ca3af',
                          fontSize: '1.5rem',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  <div style={{ padding: '1.5rem' }}>
                    {shelfProducts.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem'}}>📦</div>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '500', color: '#111827', marginBottom: '0.5rem' }}>
                          قفسه خالی است
                        </h3>
                        <p style={{ color: '#6b7280' }}>هنوز محصولی در این قفسه قرار نگرفته است</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {shelfProducts.map((shelfProduct) => (
                          <div key={shelfProduct.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '1rem',
                            backgroundColor: '#f9fafb',
                            borderRadius: '0.5rem',
                            transition: 'background-color 0.3s ease'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <div>
                                <h3 style={{ fontWeight: '500', color: '#111827', marginBottom: '0.25rem', marginLeft: '5rem' }}>
                                  {shelfProduct.product?.title || shelfProduct.product?.titleEng || 'نام محصول'}
                                </h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginLeft: '5rem' }}>
                                    SKU: {shelfProduct.product?.sku}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div style={{ textAlign: 'center', marginRight: '1rem' }}>
                              <p style={{ fontWeight: '600', color: '#111827' }}>{shelfProduct.count}</p>
                              <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                {shelfProduct.unitType === 'box' ? 'قوطی' : 'رولی'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Shelves;